import { motion } from "framer-motion";
import {
  Bot, CheckCircle2, Clock, AlertCircle,
  Brain, Bookmark,
} from "lucide-react";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

const STATUS_ICONS = {
  completed: <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" />,
  in_progress: <Clock className="w-3.5 h-3.5 text-cobalt animate-pulse" />,
  pending: <AlertCircle className="w-3.5 h-3.5 text-slate-300" />,
};

const AgentPanel = ({ activities }) => (
  <ScrollArea className="flex-1">
    <div className="py-3 px-3 space-y-2">
      {activities.length === 0 ? (
        <p className="text-xs text-muted-foreground text-center py-8">Agent activity will appear here</p>
      ) : (
        activities?.map((a, i) => (
          <motion.div
            key={`${a.agent}-${i}`}
            initial={{ opacity: 0, x: 8 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: i * 0.06 }}
            className="flex items-start gap-2.5 px-3 py-2 rounded-md bg-slate-50 border border-border/50"
          >
            <div className="mt-0.5">{STATUS_ICONS[a.status] || STATUS_ICONS.pending}</div>
            <div className="min-w-0">
              <p className="text-xs font-semibold text-foreground/80">{a.agent}</p>
              <p className="text-[11px] text-muted-foreground leading-relaxed">{a.action}</p>
            </div>
          </motion.div>
        ))
      )}
    </div>
  </ScrollArea>
);

const WorkflowPanel = ({ steps }) => (
  <ScrollArea className="flex-1">
    <div className="py-3 px-3">
      {steps.length === 0 ? (
        <p className="text-xs text-muted-foreground text-center py-8">Workflow steps will appear here</p>
      ) : (
        <div className="space-y-1">
          {steps.sort((a, b) => a.order - b.order)?.map((step, i) => (
            <div key={step.key} className="flex items-center gap-3 px-3 py-2.5 rounded-md hover:bg-slate-50">
              <div className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold shrink-0 ${
                step.status === "completed" ? "bg-emerald-100 text-emerald-700" :
                step.status === "in_progress" ? "bg-cobalt/10 text-cobalt" :
                "bg-slate-100 text-slate-400"
              }`}>
                {step.status === "completed" ? <CheckCircle2 className="w-3.5 h-3.5" /> : i + 1}
              </div>
              <div className="min-w-0">
                <p className={`text-xs font-medium ${
                  step.status === "completed" ? "text-foreground/70" :
                  step.status === "in_progress" ? "text-cobalt font-semibold" :
                  "text-muted-foreground"
                }`}>
                  {step.name}
                </p>
              </div>
              {step.status === "in_progress" && (
                <div className="ml-auto w-1.5 h-1.5 rounded-full bg-cobalt animate-pulse" />
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  </ScrollArea>
);

const MemoryPanel = ({ items }) => {
  const categories = { project: [], audience: [], preferences: [], progress: [] };
  items.forEach((item) => {
    const cat = item.category || "project";
    if (!categories[cat]) categories[cat] = [];
    categories[cat].push(item);
  });

  const CATEGORY_LABELS = { project: "Project", audience: "Audience", preferences: "Preferences", progress: "Progress" };

  return (
    <ScrollArea className="flex-1">
      <div className="py-3 px-3 space-y-4">
        {items.length === 0 ? (
          <p className="text-xs text-muted-foreground text-center py-8">Context memory will appear here</p>
        ) : (
          Object.entries(categories)?.filter(([, v]) => v.length > 0)?.map(([cat, catItems]) => (
            <div key={cat}>
              <p className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground mb-2 px-1">
                {CATEGORY_LABELS[cat] || cat}
              </p>
              <div className="space-y-1.5">
                {catItems?.map((item, i) => (
                  <div key={`${item.key}-${i}`} className="flex items-start gap-2 px-3 py-2 rounded-md bg-slate-50 border border-border/50">
                    <Bookmark className="w-3 h-3 text-cobalt/40 mt-0.5 shrink-0" />
                    <div className="min-w-0">
                      <p className="text-[11px] font-medium text-foreground/70 capitalize">{item.key?.replace(/_/g, " ")}</p>
                      <p className="text-[11px] text-muted-foreground leading-relaxed truncate">{item.value}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))
        )}
      </div>
    </ScrollArea>
  );
};

export const RightSidebar = ({ agentActivities, workflowSteps, memoryItems }) => {
  return (
    <div className="flex flex-col h-full" data-testid="right-sidebar">
      <Tabs defaultValue="agents" className="flex flex-col h-full">
        <TabsList className="w-full rounded-none border-b border-border bg-transparent h-auto p-0">
          <TabsTrigger
            value="agents"
            className="flex-1 rounded-none py-2.5 text-xs font-medium data-[state=active]:border-b-2 data-[state=active]:border-cobalt data-[state=active]:text-cobalt data-[state=active]:shadow-none"
            data-testid="tab-agents"
          >
            <Bot className="w-3.5 h-3.5 mr-1.5" /> Agents
          </TabsTrigger>
          <TabsTrigger
            value="workflow"
            className="flex-1 rounded-none py-2.5 text-xs font-medium data-[state=active]:border-b-2 data-[state=active]:border-cobalt data-[state=active]:text-cobalt data-[state=active]:shadow-none"
            data-testid="tab-workflow"
          >
            <CheckCircle2 className="w-3.5 h-3.5 mr-1.5" /> Workflow
          </TabsTrigger>
          <TabsTrigger
            value="memory"
            className="flex-1 rounded-none py-2.5 text-xs font-medium data-[state=active]:border-b-2 data-[state=active]:border-cobalt data-[state=active]:text-cobalt data-[state=active]:shadow-none"
            data-testid="tab-memory"
          >
            <Brain className="w-3.5 h-3.5 mr-1.5" /> Memory
          </TabsTrigger>
        </TabsList>
        <TabsContent value="agents" className="flex-1 mt-0 overflow-hidden">
          <AgentPanel activities={agentActivities} />
        </TabsContent>
        <TabsContent value="workflow" className="flex-1 mt-0 overflow-hidden">
          <WorkflowPanel steps={workflowSteps} />
        </TabsContent>
        <TabsContent value="memory" className="flex-1 mt-0 overflow-hidden">
          <MemoryPanel items={memoryItems} />
        </TabsContent>
      </Tabs>
    </div>
  );
};
